import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { RequestSnapshot } from '../../lib/employment/diffRequestSnapshots';
import { buildEmploymentSubmittedDisplayRows } from '../../lib/employment/employmentSubmittedDisplay';

type TableProps = {
  snapshot: RequestSnapshot | null;
  listRow?: Record<string, unknown> | null;
  evidence?: unknown;
  attemptNumber?: number | null;
  verifiedAt?: string | null;
  showIntro?: boolean;
};

export function EmployerAttemptSubmittedFieldsTable({
  snapshot,
  listRow,
  evidence,
  attemptNumber,
  verifiedAt,
  showIntro = true,
}: TableProps) {
  const rows = buildEmploymentSubmittedDisplayRows({ snapshot, listRow, evidence });
  const mutationCount = rows.filter((row) => row.mutationTracked).length;

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="employment-attempt-submitted-fields-empty">
        No customer submission fields are available for this run.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75} data-testid="employment-attempt-submitted-fields">
      {showIntro ? (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          Employment context from the verification record
          {mutationCount > 0
            ? ` · ${mutationCount} field${mutationCount === 1 ? '' : 's'} tracked for mutation compare on retry`
            : ''}
          .
        </Typography>
      ) : null}
      <TableContainer>
        {(attemptNumber != null || verifiedAt) && !showIntro ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 1 }}>
            {attemptNumber != null ? `Attempt ${attemptNumber}` : null}
            {attemptNumber != null && verifiedAt ? ' · ' : null}
            {verifiedAt ? new Date(verifiedAt).toLocaleString() : null}
          </Typography>
        ) : null}
        {(attemptNumber != null || verifiedAt) && showIntro ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 1 }}>
            {attemptNumber != null ? `Attempt ${attemptNumber}` : null}
            {attemptNumber != null && verifiedAt ? ' · ' : null}
            {verifiedAt ? new Date(verifiedAt).toLocaleString() : null}
          </Typography>
        ) : null}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: '38%' }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Submitted value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>{row.label}</span>
                    {row.mutationTracked ? (
                      <Chip
                        size="small"
                        label="mutation"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

type AccordionPaneProps = TableProps & {
  defaultExpanded?: boolean;
};

/** Detail pane: expanded table with section heading (no outer accordion). */
export function EmployerAttemptSubmittedFields({
  snapshot,
  listRow,
  evidence,
  attemptNumber,
  verifiedAt,
}: AccordionPaneProps) {
  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Customer submission
        {attemptNumber != null ? ` — Attempt ${attemptNumber}` : ''}
      </Typography>
      <EmployerAttemptSubmittedFieldsTable
        snapshot={snapshot}
        listRow={listRow}
        evidence={evidence}
        attemptNumber={attemptNumber}
        verifiedAt={verifiedAt}
        showIntro
      />
    </Stack>
  );
}
